import React, { useMemo } from "react";

/**
 * Displays the status of a React Query.
 *
 * Shows the time the query was last updated and a loading indicator while the
 * query is being refetched in the background. Intended for use with
 * `dataUpdatedAt` and `isFetching` returned from React Query's `useQuery`.
 *
 * Examples:
 * - Last updated: 14:32:18
 * - Updating... 14:32:18
 * - Last updated: 01/07/2026 14:32:18 (if the update occurred on a previous day)
 *
 * @param {object} props Component props.
 * @param {number} props.dataUpdatedAt Unix timestamp (milliseconds) from React Query's `dataUpdatedAt`.
 * @param {boolean} props.isFetching Indicates whether the query is currently fetching in the background.
 * @returns {JSX.Element}
 */
export default function QueryStatus({ dataUpdatedAt, isFetching }) {
    const lastUpdated = useMemo(() => {
        if (!dataUpdatedAt) {
            return "";
        }

        const updated = new Date(dataUpdatedAt);
        const today = new Date();

        const isToday =
            updated.getFullYear() === today.getFullYear() &&
            updated.getMonth() === today.getMonth() &&
            updated.getDate() === today.getDate();

        return updated.toLocaleString("en-ZA", {
            ...(isToday
                ? {}
                : {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                }),
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        });
    }, [dataUpdatedAt]);

    return (
        <div>
            {isFetching ? (
                <>
                    <i className="fas fa-circle-notch fa-spin mr-1" />
                    Updating...
                </>
            ) : (
                <>
                    <i className="fas fa-check-circle text-success mr-1" />
                    Updated {lastUpdated}
                </>
            )}
        </div>
    );
}
